import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faUser, 
  faCamera, 
  faSpinner,
  faCheck,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabaseClient';
import Avatar from '../ui/Avatar';
import { logger } from '../../utils/logger';

// Convert image to WebP format using Canvas API (client-side, no API needed)
const convertImageToWebP = async (file: File, quality: number = 0.85): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Create canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }
        
        // Set canvas dimensions (max 800x800 to reduce file size)
        const maxDimension = 800;
        let width = img.width;
        let height = img.height;
        
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height / width) * maxDimension;
            width = maxDimension;
          } else {
            width = (width / height) * maxDimension;
            height = maxDimension;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw image on canvas
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to WebP blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create WebP blob'));
              return;
            }
            
            // Create File from blob
            const webpFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.webp'), {
              type: 'image/webp',
              lastModified: Date.now(),
            });
            
            resolve(webpFile);
          },
          'image/webp',
          quality
        );
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

interface ProfileEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  currentName?: string;
  currentEmail?: string;
  currentPhoneNumber?: string | null;
  currentAvatarUrl?: string | null;
  onSuccess?: () => void;
}

export default function ProfileEditModal({
  isOpen,
  onClose,
  userId,
  currentName = '',
  currentEmail = '',
  currentPhoneNumber = null,
  currentAvatarUrl = null,
  onSuccess,
}: ProfileEditModalProps) {
  const queryClient = useQueryClient();
  const [fullName, setFullName] = useState(currentName);
  const [phoneNumber, setPhoneNumber] = useState(currentPhoneNumber || '');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(currentAvatarUrl);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFullName(currentName);
      setPhoneNumber(currentPhoneNumber || '');
      setAvatarPreview(currentAvatarUrl);
      setAvatarFile(null);
      setError(null);
      setSuccess(false);
    }
  }, [isOpen, currentName, currentPhoneNumber, currentAvatarUrl]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, or WebP)');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      setError('Image size must be less than 2MB');
      return;
    }

    setAvatarFile(file);
    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validar y formatear número de teléfono si se proporcionó
      let formattedPhone = phoneNumber.trim();
      if (formattedPhone) {
        // Validar formato básico
        const digits = formattedPhone.replace(/\D/g, '');
        if (digits.length < 10) {
          setError('Phone number must have at least 10 digits');
          setLoading(false);
          return;
        }

        // Formatear número (agregar + si no tiene)
        if (!formattedPhone.startsWith('+')) {
          // Si tiene 10 dígitos, asumir US (+1)
          if (digits.length === 10) {
            formattedPhone = `+1${digits}`;
          } else if (digits.length === 11 && digits.startsWith('1')) {
            formattedPhone = `+${digits}`;
          } else {
            formattedPhone = `+${digits}`;
          }
        }
      }

      let avatarUrl = currentAvatarUrl;

      // Upload avatar if changed
      if (avatarFile) {
        // Verify user is authenticated and userId matches auth.uid()
        const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
        
        if (authError || !authUser) {
          logger.error('❌ User not authenticated:', authError);
          throw new Error('You must be logged in to upload an avatar.');
        }
        
        if (authUser.id !== userId) {
          logger.error('❌ User ID mismatch:', {
            userId,
            authUid: authUser.id,
            match: authUser.id === userId
          });
          throw new Error('User ID mismatch. Please refresh the page and try again.');
        }
        
        // Convert image to WebP if it's an image file
        let fileToUpload: File = avatarFile;
        const isImage = avatarFile.type.startsWith('image/');
        
        if (isImage) {
          try {
            // Convert to WebP using Canvas API
            const webpFile = await convertImageToWebP(avatarFile);
            fileToUpload = webpFile;
            logger.log('✅ Image converted to WebP:', {
              originalSize: avatarFile.size,
              webpSize: webpFile.size,
              reduction: `${((1 - webpFile.size / avatarFile.size) * 100).toFixed(1)}%`
            });
          } catch (conversionError) {
            logger.warn('⚠️ Failed to convert image to WebP, using original:', conversionError);
            // Continue with original file if conversion fails
          }
        }
        
        const timestamp = Date.now();
        const fileName = `avatar_${timestamp}.webp`;
        const filePath = `${userId}/${fileName}`;

        logger.log('🔄 Uploading avatar:', {
          userId,
          authUid: authUser.id,
          filePath,
          fileName,
          fileSize: fileToUpload.size,
          fileType: fileToUpload.type,
          originalSize: avatarFile.size
        });

        // DELETE OLD AVATARS FIRST (before uploading new one)
        // This ensures we don't leave orphaned files
        try {
          logger.log('🔍 Listing all existing avatars in user folder:', userId);
          const { data: existingFiles, error: listError } = await supabase.storage
            .from('avatars')
            .list(userId, {
              limit: 100,
              offset: 0,
              sortBy: { column: 'created_at', order: 'desc' }
            });
          
          if (listError) {
            logger.warn('⚠️ Error listing existing avatars (non-critical):', listError);
          } else if (existingFiles && existingFiles.length > 0) {
            // Delete ALL existing avatars in the user's folder
            const filesToDelete = existingFiles.map(file => `${userId}/${file.name}`);
            
            logger.log(`🗑️ Deleting ${filesToDelete.length} existing avatar file(s) before upload:`, filesToDelete);
            
            const { error: bulkDeleteError } = await supabase.storage
              .from('avatars')
              .remove(filesToDelete);
            
            if (bulkDeleteError) {
              logger.warn('⚠️ Error deleting old avatar files (non-critical):', bulkDeleteError);
            } else {
              logger.log(`✅ Successfully deleted ${filesToDelete.length} existing avatar file(s)`);
            }
          } else {
            logger.log('ℹ️ No existing avatar files found in user folder');
          }
        } catch (cleanupError) {
          logger.warn('⚠️ Error during avatar cleanup before upload (non-critical):', cleanupError);
          // Don't throw - cleanup failure shouldn't prevent upload
        }

        // Upload new avatar AFTER deleting old ones
        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(filePath, fileToUpload, {
            cacheControl: '3600',
            upsert: false,
            contentType: 'image/webp',
          });

        if (uploadError) {
          logger.error('❌ Error uploading avatar:', uploadError);
          logger.error('Upload error details:', {
            message: uploadError.message,
            name: uploadError.name,
            statusCode: (uploadError as any).statusCode || 'N/A',
            filePath,
            userId,
            authUid: authUser.id
          });
          throw new Error(`Failed to upload avatar: ${uploadError.message}`);
        }
        
        logger.log('✅ Avatar uploaded successfully:', { filePath });

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('avatars')
          .getPublicUrl(filePath);

        avatarUrl = publicUrl;
      }

      // Update profile
      logger.log('🔄 Updating profile:', { 
        userId, 
        fullName: fullName.trim(), 
        phoneNumber: formattedPhone ? 'present' : 'none',
        avatarUrl: avatarUrl ? 'present' : 'null' 
      });
      
      // Preparar datos de actualización
      const updateData: any = {
        full_name: fullName.trim() || null,
        avatar_url: avatarUrl,
        updated_at: new Date().toISOString(),
      };

      // Solo actualizar phone_number si se proporcionó
      if (formattedPhone) {
        updateData.phone_number = formattedPhone;
      }
      
      const { error: updateError } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', userId);

      if (updateError) {
        logger.error('❌ Error updating profile:', updateError);
        logger.error('Update error details:', {
          code: updateError.code,
          message: updateError.message,
          details: updateError.details,
          hint: updateError.hint
        });
        throw new Error(`Failed to update profile: ${updateError.message}`);
      }
      
      logger.log('✅ Profile updated successfully');

      // Invalidate all user profile caches to refresh avatars everywhere
      queryClient.invalidateQueries({ queryKey: ['userProfile', userId] });
      queryClient.invalidateQueries({ queryKey: ['userProfileById', userId] });
      queryClient.invalidateQueries({ queryKey: ['userProfilesByIds'] }); // Invalidate all batch profile queries
      queryClient.invalidateQueries({ queryKey: ['userProfileByEmail'] }); // Invalidate email-based queries
      logger.log('🔄 All user profile caches invalidated');

      setSuccess(true);
      
      // Call onSuccess callback after a short delay
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      logger.error('Profile update error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[100] p-4"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl overflow-hidden w-full max-w-[320px] sm:max-w-[360px]"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100">
                <FontAwesomeIcon icon={faUser} className="text-blue-600 w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Edit Profile</h2>
                <p className="text-sm text-gray-600">Update your profile information</p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={loading}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Avatar Upload */}
            <div className="flex flex-col items-center space-y-4">
              <div className="relative">
                <Avatar
                  src={avatarPreview}
                  name={fullName}
                  email={currentEmail}
                  size="xl"
                />
                <label
                  htmlFor="avatar-upload"
                  className="absolute bottom-0 right-0 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faCamera} className="w-4 h-4" />
                </label>
                <input
                  id="avatar-upload"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleAvatarChange}
                  disabled={loading}
                  className="hidden"
                />
              </div>
              <p className="text-xs text-gray-500 text-center">
                Click the camera icon to upload a new avatar
                <br />
                (JPEG, PNG, or WebP • Max 2MB)
              </p>
            </div>

            {/* Full Name Input */}
            <div>
              <label htmlFor="full-name" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <input
                id="full-name"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={loading}
                placeholder="Enter your full name"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            {/* Email (Read-only) */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={currentEmail}
                disabled
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">
                Email cannot be changed here
              </p>
            </div>

            {/* Phone Number Input */}
            <div>
              <label htmlFor="phone-number" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                id="phone-number"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                disabled={loading}
                placeholder="+1 (123) 456-7890"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">
                Used for support calls and notifications. Format: +1 234 567 8900
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                <FontAwesomeIcon icon={faCheck} className="w-4 h-4 flex-shrink-0" />
                <span>Profile updated successfully!</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || success}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Save Changes</span>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

